// NOTE: If we add P2R stores in additional timezones, those timezones need to be added here.
// At some point we should consider storing the localized pickup time in the order, so we do
// not need to do this conversion here
const TZ_ABBR_TO_OFFSET_MINUTES: Record<string, number> = {
  EST: -5 * 60,
  EDT: -4 * 60,
  CST: -6 * 60,
  CDT: -5 * 60,
  MST: -7 * 60,
  MDT: -6 * 60,
  PST: -8 * 60,
  PDT: -7 * 60,
  AKST: -9 * 60,
  AKDT: -8 * 60,
  HST: -10 * 60,
  HDT: -9 * 60,
};

export function formattedPickupDateFromUTC(
  inputDateTime: string, // Should be in UTC format, e.g., "2025-11-26T10:30:00Z"
  tzAbbr: string,
  locale: string = 'en-US'
): string {
  if (!inputDateTime) return '';

  const date = new Date(inputDateTime);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  // By default, use the user's local timezone
  const normalizedTzAbbr = tzAbbr.trim().toUpperCase();
  let localizedDate = date;
  let formatOptions = {};
  let tzAbbrToDisplay =
    new Intl.DateTimeFormat(locale, { timeZoneName: 'short' })
      .formatToParts(new Date())
      .find(part => part.type === 'timeZoneName')
      ?.value ?? normalizedTzAbbr;

  // If we know have the input timezone in our map above, then add the offset to the UTC date, 
  // and do all our formatting in UTC, so the time is correct for the input timezone
  if (normalizedTzAbbr in TZ_ABBR_TO_OFFSET_MINUTES) {
    const offsetMinutes = TZ_ABBR_TO_OFFSET_MINUTES[normalizedTzAbbr];
    localizedDate = new Date(date.getTime() + offsetMinutes * 60000);
    formatOptions = { timeZone: 'UTC' };
    tzAbbrToDisplay = normalizedTzAbbr;
  }

  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short', ...formatOptions }).format(localizedDate);
  const month = new Intl.DateTimeFormat(locale, { month: 'short', ...formatOptions }).format(localizedDate);
  const day = new Intl.DateTimeFormat(locale, { day: 'numeric', ...formatOptions }).format(localizedDate);
  const year = new Intl.DateTimeFormat(locale, { year: 'numeric', ...formatOptions }).format(localizedDate);

  const timeParts = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    ...formatOptions,
  }).formatToParts(localizedDate);

  const hour = timeParts.find(p => p.type === 'hour')?.value ?? '';
  const minute = timeParts.find(p => p.type === 'minute')?.value ?? '';
  const ampm = (timeParts.find(p => p.type === 'dayPeriod')?.value ?? '').toUpperCase();

  return `${hour}:${minute} ${ampm} (${tzAbbrToDisplay}) ${weekday}, ${month} ${day}, ${year}`;
}

export function formattedPickupDate(
  inputDateTime: string,
  tzAbbr: string,
  locale: string = 'en-US',
): string {
  if (!inputDateTime) return '';

  const date = new Date(inputDateTime.replace(' ', 'T'));

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date);
  const month = new Intl.DateTimeFormat(locale, { month: 'short' }).format(date);
  const day = new Intl.DateTimeFormat(locale, { day: 'numeric' }).format(date);

  const timeParts = new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date);

  const hour = timeParts.find(p => p.type === 'hour')?.value ?? '';
  const minute = timeParts.find(p => p.type === 'minute')?.value ?? '';
  const ampm = (timeParts.find(p => p.type === 'dayPeriod')?.value ?? '').toUpperCase();

  return `${hour}:${minute} ${ampm} (${tzAbbr}) ${weekday}, ${month} ${day}`;
}


export function formatLocalDate(dateString:string, locale:string = "en-US") {
  const date = new Date(dateString);

  const weekday = date.toLocaleDateString(locale, { weekday: "short" });
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${weekday} ${month}/${day}`;
}

export const formatDateTimeByLocale = (
  date: Date | string,
  locale: string,
): string => {
  const dateValue = typeof date === 'string' ? new Date(date) : date;

  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).format(dateValue);
};

export const formatDateTimeByLocaleWithoutDays = (
  date: Date | string,
  locale: string,
): string => {
  const dateValue = typeof date === 'string' ? new Date(date) : date;

  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).format(dateValue);
};

export const formatDateTimeWithTimezone = (
  date: Date | string,
  locale: string,
): string => {
  const dateValue = typeof date === 'string' ? new Date(date) : date;

  const dateTime = new Intl.DateTimeFormat(locale, {
    dateStyle: 'short',
    timeStyle: 'medium',
  })?.format(dateValue);

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const offset = new Intl.DateTimeFormat('en', {
    timeZone,
    timeZoneName: 'shortOffset',
    hour: 'numeric',
  })
    .formatToParts(dateValue)
    .find(part => part.type === 'timeZoneName')?.value;
    
  return `${dateTime} (${offset})`;
};

export const formatDayAndDateByLocale = (
  input: string,
  locale: string,
  year = new Date().getFullYear(),
) => {
  const match = input.match(/(\d{1,2})\/(\d{1,2})/);

  if (!match) {
    return '';
  }

  const month = Number(match[1]);
  const day = Number(match[2]);

  const date = new Date(Date.UTC(year, month - 1, day));

  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date);
};


